import { Box, Button, List, ListItem } from "@mui/material"
import { keyBy } from "lib/keyBy"
import { ListItemBox } from "lib/@components/ListItemBox"
import { pick } from "lib/pick"
import { inTransition, noChange } from "lib/@hooks/useRefresh"
import { HeaderBar } from "routes/schedule/compare/header-bar"
import { Moved } from "./Moved"
import { NewItem } from "./NewItem"
import { registerComparitor } from "./register-comparitor"
import { DifferenceWrapper } from "routes/schedule/compare/differenceWrapper"
import { AcceptedChanged, HasMoved, IsAccepted } from "event-definitions"
import { indexTasksInSchedule } from "routes/schedule/lib/index-tasks-in-schedule"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { findTask } from "routes/schedule/compare/find-task"
import { isNotHeader } from "routes/schedule/compare/is-not-header"
import { OnlyWithoutComparisonReadOnly } from "./only-without-comparison-read-only"

registerComparitor("task-order-comparitor", ({ compare, to, differences }) => {
    // Key by the stable taskId (UUID) — `id` is positional ("scheduleId.position")
    // and collides between versions whenever tasks are inserted or removed.
    const compareTasks = keyBy(
        compare.tasks,
        pick((t) => t.taskId ?? t.id)
    )
    const toTasks = keyBy(
        to.tasks,
        pick((t) => t.taskId ?? t.id)
    )
    const movements = []
    let anyNew = false
    const taskOrder = compare.tasks.filter(isNotHeader).sortBy("$index").map("id")
    const oldOrder = to.tasks.filter(isNotHeader).sortBy("$index").map("id")
    for (const task of Object.values(compareTasks).filter(isNotHeader).sortBy("$index")) {
        const comparedTo = findTask(toTasks, task)
        if (comparedTo) {
            if (comparedTo.$index !== task.$index) {
                movements.push(<Moved task={task} order={() => task.$sort} from={comparedTo.$sort + 1} key={task.id} />)
            }
        } else {
            anyNew = true
            movements.push(<NewItem task={task} order={() => task.$sort} key={task.id} />)
        }
    }
    if (movements.length) {
        differences.push(
            <HeaderBar label="Task Order" alwaysShow>
                <Movements
                    anyNew={anyNew}
                    movements={movements}
                    originalOrder={taskOrder}
                    oldOrder={oldOrder}
                    componentType="Movements"
                />
            </HeaderBar>
        )
    }
})

function Movements({ movements, originalOrder, oldOrder }) {
    const { target, noIndividualEditor, canSave } = useBoundContext()
    HasMoved.useRefresh(inTransition)
    const inOrder = Object.isEqual(originalOrder, target.tasks.sortBy("$sort").map("id"))
    const sorted = movements.sortBy((m) => m.props.order())
    const isAccepted = IsAccepted.call({ isAccepted: false, id: "movements" })
    AcceptedChanged.after.useRefresh(noChange)
    return (
        <DifferenceWrapper label="Task Order">
            <List key={sorted.map("key")}>
                <ListItem>
                    <ListItemBox fontWeight="bold">
                        <Box flex={1}>Task Title</Box>
                        <Box width={125}>Previous Order</Box>
                    </ListItemBox>
                </ListItem>

                {sorted}
            </List>
            {!noIndividualEditor && !isAccepted && canSave && (
                <OnlyWithoutComparisonReadOnly>
                    <ListItemBox>
                        <Box flex={1} />
                        <Box>
                            {inOrder ? (
                                <Button variant="outlined" onClick={revert}>
                                    Revert
                                </Button>
                            ) : (
                                <Button variant="contained" color="secondary" onClick={restore}>
                                    Restore
                                </Button>
                            )}
                        </Box>
                    </ListItemBox>
                </OnlyWithoutComparisonReadOnly>
            )}
        </DifferenceWrapper>
    )

    function revert() {
        target.tasks = target.tasks.sortBy((t) => fixIndex(oldOrder.indexOf(t.id)))
        indexTasksInSchedule(target)
        HasMoved.raise()
    }

    function restore() {
        target.tasks = target.tasks.sortBy((t) => fixIndex(originalOrder.indexOf(t.id)))
        indexTasksInSchedule(target)
        HasMoved.raise()
    }
}

function fixIndex(index) {
    return index < 0 ? Infinity : index
}
